import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, BedDouble, Ruler, Weight, Fuel, Gauge, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { SITE, formatPrice, statusLabel, typeLabel } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import BookingDialog from "@/components/BookingDialog";
import VehicleCard from "@/components/VehicleCard";

const SPEC_ICONS = {
  Sovplatser: BedDouble,
  Längd: Ruler,
  Totalvikt: Weight,
  Drivmedel: Fuel,
  Mätarställning: Gauge,
};

const URL_RE = /(https?:\/\/[^\s]+)/g;

function renderDescription(text = "") {
  return text
    .split(URL_RE)
    .map((part, i) =>
      i % 2 === 1 ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-electric underline hover:text-white transition-colors break-all"
          data-testid={`description-link-${i}`}
        >
          {part}
        </a>
      ) : (
        <span key={i}>{part}</span>
      )
    );
}

export default function FordonDetail() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setVehicle(null);
    setNotFound(false);
    setActive(0);
    api
      .get(`/vehicles/${id}`)
      .then(async (r) => {
        setVehicle(r.data);
        const all = await api.get("/vehicles");
        setRelated(all.data.filter((v) => v.id !== id && v.vtype === r.data.vtype).slice(0, 3));
      })
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-40 pb-24 text-center">
        <p className="font-display font-bold text-2xl text-white">Fordonet hittades inte</p>
        <p className="mt-2 text-slate-400">Den kan vara borttagen.</p>
        <Link to="/fordon" data-testid="detail-back-after-404" className="mt-6 inline-block rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white">
          Tillbaka till fordonen
        </Link>
      </main>
    );
  }

  if (!vehicle) {
    return <main className="pt-40 pb-24 text-center text-slate-400" data-testid="detail-loading">Laddar fordon...</main>;
  }

  const sold = vehicle.status === "salt";
  const images = vehicle.images?.length ? vehicle.images : [""];

  return (
    <main className="pt-28 md:pt-36 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/fordon" data-testid="detail-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={16} />
          Alla fordon
        </Link>

        <div className="mt-8 grid gap-10 lg:grid-cols-[1.5fr_1fr] items-start">
          {/* GALLERY */}
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-metallic/70 bg-surface select-none" data-testid="detail-gallery">
              <motion.div
                key={active}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.18}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -60 && active < images.length - 1) setActive(active + 1);
                  else if (info.offset.x > 60 && active > 0) setActive(active - 1);
                }}
                className="cursor-grab active:cursor-grabbing"
                style={{ touchAction: "pan-y" }}
                initial={{ opacity: 0.55 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
                data-testid="detail-swipe-area"
              >
                <img
                  src={images[active]}
                  alt={vehicle.title}
                  draggable={false}
                  className={`w-full aspect-[16/10] object-cover pointer-events-none ${sold ? "grayscale-[0.6]" : ""}`}
                />
              </motion.div>
              <span
                data-testid="detail-status-badge"
                className={`absolute top-5 left-5 rounded-full px-4 py-1.5 font-mono-brand text-xs font-semibold tracking-widest backdrop-blur border ${
                  sold
                    ? "bg-red-500/15 text-red-400 border-red-400/30"
                    : "bg-emerald-500/15 text-emerald-400 border-emerald-400/30"
                }`}
              >
                {statusLabel(vehicle.status).toUpperCase()}
              </span>
              {images.length > 1 && (
                <>
                  <button
                    data-testid="detail-prev-button"
                    onClick={() => setActive((active - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-ink/70 backdrop-blur text-white hover:bg-brand transition-colors"
                    aria-label="Föregående bild"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    data-testid="detail-next-button"
                    onClick={() => setActive((active + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-ink/70 backdrop-blur text-white hover:bg-brand transition-colors"
                    aria-label="Nästa bild"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="detail-dots">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActive(i)}
                        className={`h-2 rounded-full transition-all ${i === active ? "w-6 bg-electric" : "w-2 bg-white/60"}`}
                        aria-label={`Bild ${i + 1}`}
                        data-testid={`detail-dot-${i}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3" data-testid="detail-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`detail-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${
                      active === i ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="aspect-[16/10] w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          {/* INFO */}
          <div>
            <Reveal delay={0.1}>
              <p className="font-mono-brand text-[11px] tracking-[0.25em] text-slate-400 uppercase">
                {typeLabel(vehicle.vtype)} · Årsmodell {vehicle.year}
              </p>
              <h1 className="mt-2 font-display font-black tracking-tight text-3xl md:text-4xl text-white" data-testid="detail-title">
                {vehicle.title}
              </h1>
              <p className="mt-4 font-mono-brand font-bold text-3xl text-electric" data-testid="detail-price">
                {formatPrice(vehicle.price)}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <dl className="mt-8 rounded-2xl border border-metallic/70 bg-surface divide-y divide-metallic/60 overflow-hidden" data-testid="detail-specs">
                {Object.entries(vehicle.specs || {}).map(([k, v]) => {
                  const Icon = SPEC_ICONS[k];
                  return (
                    <div key={k} className="flex items-center justify-between px-5 py-3.5">
                      <dt className="flex items-center gap-3 text-sm text-slate-400">
                        {Icon && <Icon size={15} className="text-electric" />}
                        {k}
                      </dt>
                      <dd className="font-mono-brand text-sm text-white">{v}</dd>
                    </div>
                  );
                })}
              </dl>
            </Reveal>

            {vehicle.description && (
              <Reveal delay={0.2}>
                <p className="mt-6 leading-relaxed text-slate-300 whitespace-pre-line" data-testid="detail-description">
                  {renderDescription(vehicle.description)}
                </p>
              </Reveal>
            )}

            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={`tel:${SITE.phone}`}
                  data-testid="detail-phone-button"
                  className="inline-flex items-center gap-2 rounded-full border border-metallic px-7 py-3.5 font-semibold text-slate-200 hover:border-electric hover:text-electric transition-colors"
                >
                  <Phone size={16} />
                  Ring oss
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-24" data-testid="related-section">
            <h2 className="font-display font-bold text-2xl text-white mb-8">Liknande fordon</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {related.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
